import {useState} from 'react';

import {assertAndContinue} from 'Asserts';
import {OrganizationDefaultSchemaCollaboratorsConfiguration} from 'auth/__generated/OrganizationDefaultSchemaCollaboratorsConfiguration';
import {
  Collaborator,
  convertToCollaborators,
  isSchemaPolicyActorCollaborator,
  isSchemaPolicyGroupCollaborator,
  SchemaCollaborator,
} from 'auth/PermissionConstants';
import {DefaultSchemaPoliciesConfig} from 'auth/PolicyConstants';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {DefaultSchemaCollaboratorsConfigurationSchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import EntityStore from 'stores/EntityStore';

type DefaultSchemaCollaboratorsConfiguration =
  OrganizationDefaultSchemaCollaboratorsConfiguration['schemaCollaborationDefaultConfigurations'][number];

type SchemaCollaboratorsAction = (collaborators: Array<Collaborator>) => void;

interface SchemaCollaboratorsState {
  collaborators: Array<Collaborator>;
  minimalCollaborators: Array<SchemaCollaborator>;
}

function toMinimalCollaborators(collaborators: Array<Collaborator>): Array<SchemaCollaborator> {
  return collaborators
    .map((collaborator) => {
      if (isSchemaPolicyActorCollaborator(collaborator)) {
        return {
          id: collaborator.id,
          schemaPolicyId: collaborator.schemaPolicyId,
        };
      }
      if (isSchemaPolicyGroupCollaborator(collaborator)) {
        return {
          id: collaborator.id,
          adminSchemaPolicyId: collaborator.adminSchemaPolicyId,
          memberSchemaPolicyId: collaborator.memberSchemaPolicyId,
        };
      }
      assertAndContinue(false, `Unexpected collaborator type ${collaborator}`);
      return null;
    })
    .filter((collaborator: SchemaCollaborator | null): collaborator is SchemaCollaborator =>
      Boolean(collaborator)
    );
}

function getCreatorCollaborator(schemaPolicyConfig: DefaultSchemaPoliciesConfig): Collaborator {
  return {...EntityStore.currentUser(), schemaPolicyId: schemaPolicyConfig.creator_schema_policy_id};
}

function getOwnerOrgCollaborator(
  organization: OrganizationDefaultSchemaCollaboratorsConfiguration,
  schemaPolicyConfig: DefaultSchemaPoliciesConfig,
  schemaType: DefaultSchemaCollaboratorsConfigurationSchemaType
): Collaborator {
  const memberSchemaPolicyId =
    schemaPolicyConfig.org_members_policy_id ??
    (schemaType === DefaultSchemaCollaboratorsConfigurationSchemaType.INTERFACE
      ? schemaPolicyConfig.read_policy_id
      : schemaPolicyConfig.create_policy_id);
  return {
    handle: organization.handle,
    id: organization.id,
    name: organization.name,
    type: ModelTypeConstants.ORGANIZATION,
    owner: true,
    adminSchemaPolicyId: schemaPolicyConfig.admin_policy_id,
    memberSchemaPolicyId,
  };
}

export function getDefaultCollaborators(
  organization: OrganizationDefaultSchemaCollaboratorsConfiguration,
  schemaType: DefaultSchemaCollaboratorsConfigurationSchemaType = DefaultSchemaCollaboratorsConfigurationSchemaType.ENTITY
): Array<Collaborator> {
  const schemaPolicyConfig = BenchlingConfig.PERMISSIONS_DEFAULT_SCHEMA_POLICIES;
  if (!schemaPolicyConfig) {
    return [];
  }
  // Keep in sync with benchling/auth/permissions/schemas/get_initial_specs.py
  const collaborators: Array<Collaborator> = [getCreatorCollaborator(schemaPolicyConfig)];

  if (BenchlingConfig.PERMISSIONS_ENABLE_SCHEMA_ACCESS_POLICIES_SETTINGS_TAB) {
    const defaultSchemaCollaboratorsConfiguration =
      organization.schemaCollaborationDefaultConfigurations.find(
        (configuration: DefaultSchemaCollaboratorsConfiguration) => configuration.schemaType === schemaType
      );
    assertAndContinue(
      defaultSchemaCollaboratorsConfiguration != null,
      `This organization must have a default schema configuration for ${pluralize(
        2,
        schemaType.toLowerCase()
      )}`
    );
    if (defaultSchemaCollaboratorsConfiguration == null) {
      return [
        getCreatorCollaborator(schemaPolicyConfig),
        getOwnerOrgCollaborator(organization, schemaPolicyConfig, schemaType),
      ];
    }

    const collaboratorsUnordered = convertToCollaborators(
      defaultSchemaCollaboratorsConfiguration,
      organization
    );

    // add owner first so its second in the list behind creator
    collaborators.push(collaboratorsUnordered.find((c) => c.id === organization.id)!);
    for (const c of collaboratorsUnordered) {
      if (c.id !== organization?.id) {
        collaborators.push(c);
      }
    }
  } else {
    collaborators.push(getOwnerOrgCollaborator(organization, schemaPolicyConfig, schemaType));
  }
  return collaborators;
}

/**
 * React hook to track state of SchemaCollaboratorsSection and cross-state logic
 */
export function useSchemaCollaboratorsSectionWithDefaults(
  organization: OrganizationDefaultSchemaCollaboratorsConfiguration,
  schemaType: DefaultSchemaCollaboratorsConfigurationSchemaType = DefaultSchemaCollaboratorsConfigurationSchemaType.ENTITY
): [SchemaCollaboratorsState, SchemaCollaboratorsAction] {
  return useSchemaCollaboratorsSection(() => getDefaultCollaborators(organization, schemaType));
}

export function useSchemaCollaboratorsSection(
  initialCollaborators: Array<Collaborator> | (() => Array<Collaborator>)
): [SchemaCollaboratorsState, SchemaCollaboratorsAction] {
  const [collaborators, setCollaborators] = useState(initialCollaborators);
  const [minimalCollaborators, setMinimalCollaborators] = useState(toMinimalCollaborators(collaborators));

  function handleUpdateCollaborators(collaborators: Array<Collaborator>): void {
    setCollaborators(collaborators);
    setMinimalCollaborators(toMinimalCollaborators(collaborators));
  }
  return [{collaborators, minimalCollaborators}, handleUpdateCollaborators];
}
